/**
 * What the inspector panel is currently showing.
 *
 * Four shapes, because the things a user can click mean different things:
 *
 *   - an address and a transaction are SUBJECTS — they can be attributed, and
 *     the panel offers write affordances;
 *   - an edge is a RELATIONSHIP, and a catalog assignment has nowhere to land on
 *     one, so its panel is read-only and hands the user off to its endpoints;
 *   - an {@link InspectorNode} is a node with no subject behind it — an
 *     off-chain actor, a leg the decode expects but has not seen, a node an
 *     analyst added. It cannot be attributed but it is not empty, and refusing
 *     to open it left a third of the canvas inert to the click;
 *   - an {@link InspectorCollection} is SEVERAL elements drawn as one — a
 *     bundled edge, a collapsed cluster, a grouping band. The question there is
 *     "what is in this", which is a list, not a detail view.
 *
 * A subject is deliberately an {@link AttributionSubject} plus a display label
 * rather than a parallel type: the panel passes it straight to the attribution
 * controller, and two near-identical types would drift.
 */
import type { TxTenancyScopeId } from "@authe/fe-commons/tenancy";
import type { SceneElementOrigin } from "@authe/fe-commons/models";
import { nodeKindLabel } from "@authe/fe-commons/display";
import type { FocusTarget } from "@authe/fe-commons/focus";
import { normalizeAddress, validatorIndexOf, validatorLabel } from "@authe/fe-commons/chains";
import type { AttributionSubject } from "@/features/attribution";

/**
 * An inspectable, attributable thing.
 *
 * Narrowed to the two arms this panel can actually show. `AttributionSubject`
 * covers every scope the backend can attribute — an entity, an event, an
 * internal transfer — but the inspector renders an address header or a
 * transaction header and nothing else, so widening it here would only mean
 * `subjectFromFocus` returning a shape the panel cannot draw. Still an
 * `AttributionSubject`, so it passes straight to the controller.
 */
export type InspectorSubject = Extract<
  AttributionSubject,
  { kind: "address" | "transaction" }
> & { label?: string; scope?: TxTenancyScopeId };

/** One read-only fact (value moved, direction, protocol, call count…). */
export interface InspectorFact {
  label: string;
  value: string;
}

export interface InspectorEdge {
  kind: "edge";
  id: string;
  label?: string;
  /**
   * Why the two ends are connected, in one sentence: what moved or what was
   * carried, by which protocol, and whether the chain reported it or the
   * grouping inferred it. First thing the panel says about an arrow.
   */
  relationship?: string;
  /**
   * The endpoints, when they resolve to something inspectable: an address (an
   * attributable subject) or an off-chain node (a bridge, a DON), which has no
   * address but is still what the arrow runs to.
   */
  from?: InspectorSubject | InspectorNode;
  to?: InspectorSubject | InspectorNode;
  facts?: InspectorFact[];
  /**
   * `decoded` (observed) / `synthesized` (inferred) / `user` (drawn by an
   * analyst). An authored edge asserts a relationship nobody observed, so the
   * panel leads with the claim behind it rather than with its (absent) amounts.
   */
  origin?: SceneElementOrigin;
  /**
   * The transactions this arrow stands for, each one openable.
   *
   * A graph over several transactions folds repeat transfers between one pair
   * onto a single arrow, and the arrow's count said "three" without saying
   * which three — so checking them meant finding them in the table by hand.
   * Absent when the scene did not say (a one-transaction graph is about the
   * transaction the page is already showing).
   */
  transactions?: InspectorSubject[];
  /**
   * How many movements the arrow stands for. Can exceed `transactions.length`:
   * the scene carries a bounded list, and a fold built before references were
   * recorded carries a count and no list. The panel says how many it could not
   * name rather than presenting a short list as the whole.
   */
  movementCount?: number;
}

/**
 * A node with no attributable subject behind it.
 *
 * Everything here is already in the scene the canvas drew from, so opening one
 * costs no read. That is the point: the reason these never opened was not that
 * the data was missing, it was that the panel only knew how to key off an
 * address.
 */
export interface InspectorNode {
  kind: "node";
  id: string;
  label?: string;
  /** The scene's own kind — `bridge`, `don`, `virtual`, `entity`, … */
  nodeKind: string;
  /**
   * Off-chain infrastructure, as the scene declared it.
   *
   * Carried because it is what disambiguates a kind the registry has never seen.
   * `nodeKindLabel` reads an unknown ON-chain kind as its own tidied spelling and
   * an unknown OFF-chain one as an `Entity` — a thing the pipeline could not name
   * is still not an account — and without this the panel could only ever make the
   * first guess. That put it one word out of step with the canvas for exactly the
   * kinds nobody has mapped yet, which are the ones a reader most needs the
   * panel's help with.
   */
  offChain?: boolean;
  /** `decoded` (observed) / `synthesized` (inferred) / `user` (added by hand). */
  origin: SceneElementOrigin;
  chainId?: string;
  facts?: InspectorFact[];
}

/** What a collection collapses: parallel flows, clustered nodes, a band. */
export type InspectorCollectionKind = "bundle" | "cluster" | "group";

export interface InspectorCollectionMember {
  id: string;
  label: string;
  sublabel?: string;
  /** Open this member. Absent when it resolves to nothing inspectable. */
  target?: InspectorTarget;
}

export interface InspectorCollection {
  kind: "collection";
  id: string;
  label?: string;
  of: InspectorCollectionKind;
  members: InspectorCollectionMember[];
  facts?: InspectorFact[];
}

/**
 * A detector finding — one row of the finding journal, opened beside the list
 * it was clicked in.
 *
 * A subject in the URL sense (it exists independently of any page, so a link
 * to it resolves anywhere) but not an attribution subject: nothing is
 * attributed TO a finding, its page is the triage and the evidence. The panel
 * renders the same `FindingDetail` the group page does, with a way to the full
 * page.
 */
export interface InspectorFinding {
  kind: "finding";
  id: string;
  /** The tier the finding is filed at (`system`, `tenant`, …), for its page. */
  scope?: string | null;
  label?: string;
}

export type InspectorTarget =
  | InspectorSubject
  | InspectorFinding
  | InspectorEdge
  | InspectorNode
  | InspectorCollection;

/** One flow touching the inspected node, as the connections list shows it. */
export interface InspectorConnection {
  edge: InspectorEdge;
  direction: "in" | "out";
  /** The node at the other end — present even when it isn't inspectable. */
  counterpartyId: string;
  counterparty?: InspectorSubject;
}

/** A graph element the authoring actions can name. */
export interface GraphElementRef {
  kind: "node" | "edge";
  id: string;
}

/**
 * What the panel can do to the thing it is showing *as a graph element*, as
 * opposed to as a subject.
 *
 * Renaming and hiding are annotations about a picture, not facts about an
 * address, which is why they are separate from {@link InspectorSubject}: the
 * same address inspected from the table is the same subject with no graph
 * element behind it, and the panel must then offer neither.
 *
 * Absent when the host has no graph, so every field can be required.
 */
export interface InspectorGraphApi {
  /** The inspected target's id on the graph, if it is on the graph. */
  element?: GraphElementRef;
  /** Flows touching {@link InspectorGraphApi.element}, when it is a node. */
  connections: InspectorConnection[];
  /** Whether this element's label came from an analyst rather than the decode. */
  relabelled?: boolean;
  /** True when the element is the analyst's own addition, not the decode's. */
  authored?: boolean;
  /**
   * Why this element is on the graph, in the author's words — the claim behind
   * an authored element, or the justification typed onto a derived one.
   *
   * Read back so it can be shown and revised. It was written and never read,
   * which made a reason effectively write-once: typed into the prompt that fires
   * seconds after the gesture, then invisible to the person who reads the
   * annotation later and cannot ask the author.
   */
  reason?: string;
  canEdit: boolean;
  onRelabel: (element: GraphElementRef, label: string) => void;
  onHide: (element: GraphElementRef) => void;
  /**
   * Record (or clear, with an empty string) why the element is on the graph.
   *
   * Separate from {@link InspectorGraphApi.onRelabel} because a name and a
   * reason answer different questions and only the reason travels: "off-ramp" is
   * a name, "receives from three mules within an hour and never sends back" is
   * why anyone should believe it.
   */
  onExplain?: (element: GraphElementRef, reason: string) => void;
  /**
   * Delete an AUTHORED element outright.
   *
   * The panel could rename and hide and nothing else, so a link an analyst drew
   * — by definition their own, asserting something nobody observed — could never
   * be taken back from here. Absent for a derived element, which is hidden
   * rather than deleted; the decode is evidence.
   */
  onRemove?: (element: GraphElementRef) => void;
  /**
   * Take back the rename on a DERIVED element, restoring the decoded name.
   * Offered only where {@link InspectorGraphApi.relabelled} is true.
   */
  onRevert?: (element: GraphElementRef) => void;
  /** Show another element — a connection, or an edge's endpoint. */
  onSelect: (target: InspectorTarget) => void;
}

export function isEdgeTarget(target: InspectorTarget): target is InspectorEdge {
  return target.kind === "edge";
}

/** True for the two arms the attribution controls can write to. */
export function isSubjectTarget(target: InspectorTarget): target is InspectorSubject {
  return target.kind === "address" || target.kind === "transaction";
}

/**
 * The focus store's selection as something the inspector can show.
 *
 * Returns `null` for the three catalog focus kinds (entity / label / tag / group)
 * — they are attribution VALUES, not subjects, so there is nothing to attribute
 * them to — and for an address whose chain is unknown, since every address read
 * and every address write is keyed by `(chain, address)` and a panel that guessed
 * the chain would quietly show (or worse, tag) the wrong one.
 */
export function subjectFromFocus(focus: FocusTarget | null | undefined): InspectorSubject | null {
  if (!focus) return null;
  if (focus.kind === "transaction") {
    return { kind: "transaction", canonicalTxId: focus.id, label: focus.label };
  }
  if (focus.kind === "address" && focus.chain) {
    return { kind: "address", chainId: focus.chain, address: focus.id, label: focus.label };
  }
  return null;
}

/**
 * The focus store's view of a subject, for navigating from an edge endpoint.
 *
 * The address is folded as `focusAddress` folds it, never lower-cased: the id is
 * what gets opened, and a lower-cased Solana address is an account that does not
 * exist.
 */
export function focusFromSubject(subject: InspectorSubject): FocusTarget {
  return subject.kind === "transaction"
    ? { kind: "transaction", id: subject.canonicalTxId, label: subject.label }
    : {
        kind: "address",
        id: normalizeAddress(subject.address, subject.chainId),
        chain: subject.chainId,
        label: subject.label,
      };
}

/** Stable identity, so a re-render with an equal target does not refetch. */
export function targetKey(target: InspectorTarget): string {
  switch (target.kind) {
    case "edge":
      return `edge:${target.id}`;
    case "node":
      return `node:${target.id}`;
    case "collection":
      return `collection:${target.id}`;
    case "transaction":
      return `transaction:${target.canonicalTxId}${target.scope ? `:${target.scope}` : ""}`;
    case "finding":
      return `finding:${target.id}`;
    default:
      return `address:${target.chainId}:${normalizeAddress(target.address, target.chainId)}`;
  }
}

const COLLECTION_EYEBROW: Record<InspectorCollectionKind, string> = {
  bundle: "Bundled flows",
  cluster: "Cluster",
  group: "Group",
};

/**
 * What kind of thing this is, in the reader's terms.
 *
 * A plain node kind is not enough for the arms that have no subject: "virtual"
 * and "entity" are our vocabulary, not the analyst's, and the distinction that
 * matters to them is whether the thing was observed, inferred, or put there by a
 * colleague.
 */
export function targetEyebrow(target: InspectorTarget): string {
  switch (target.kind) {
    case "address":
      // A beacon-chain validator travels the graph as an address-shaped node,
      // and is not an account: calling it one is the mistake its name prevents.
      return validatorIndexOf(target.address) !== null ? "Validator" : "Address";
    case "transaction":
      return "Transaction";
    case "finding":
      return "Finding";
    case "edge":
      // Worth distinguishing: everything else on the canvas is something the
      // chain reported, this one is a colleague's assertion — or the grouping
      // engine's inference (a cross-chain reference, an expected hop).
      return target.origin === "user" ? "Authored link" : target.origin === "synthesized" ? "Inferred link" : "Flow";
    case "collection":
      return COLLECTION_EYEBROW[target.of];
    default:
      return nodeEyebrow(target);
  }
}

function nodeEyebrow(node: InspectorNode): string {
  if (node.origin === "user") return "Added node";
  if (node.origin === "synthesized") return "Expected step";
  // The node-kind registry's own name for it. This used to prettify the raw
  // wire string here — a third copy of that trick, after the graph's card and
  // its grouping bands — so the panel titled a DON "Don" and a wallet "Eoa"
  // while the card it was opened from said "Oracle network" and "Wallet".
  //
  // It hand-rolled one because the registry used to live in `@authe/tx-graph`,
  // and a side panel has no business importing a canvas renderer to learn a
  // word. The domain half is in `fe-commons/display` for exactly this.
  return nodeKindLabel(node.nodeKind, { offChain: node.offChain }) ?? "Node";
}

/** Header text — the caller's label when it has one, else the raw identifier. */
export function targetTitle(target: InspectorTarget): string {
  if (target.label) return target.label;
  switch (target.kind) {
    case "address": {
      const validator = validatorIndexOf(target.address);
      return validator !== null ? validatorLabel(validator) : target.address;
    }
    case "transaction":
      return target.canonicalTxId;
    case "finding":
      return "Finding";
    case "node":
      return target.id;
    case "collection":
      return `${target.members.length} ${target.of === "bundle" ? "flows" : "nodes"}`;
    default:
      // A just-drawn link has no label yet, and the form below the header is
      // where it gets one.
      return target.origin === "user" ? "New link" : "Flow";
  }
}
